import {
  FilterBar,
  ListEmpty,
  NumberedPager,
  useListUrlState,
  rangeForPreset,
  windowLabel,
  inRange,
  applySearch,
  sortBy,
  paginate,
  DEFAULT_HISTORY_PRESET,
  type HistoryDatePreset,
} from '@/components/lists';
import type { Check } from '../types';
import { money } from '../utils';
import { DC_CHECK_FIGURES } from '../fixtures';

interface Props {
  /** The big Checks history (§9). */
  rows: Check[];
  /** Missing checks — pinned above the paged list, across every month. */
  missing: Check[];
  hasStatement: boolean;
  onUploadStatement: () => void;
  onOpenMissingHitl: () => void;
}

const PAGE_SIZE = 50;

function checkText(c: Check): string {
  return `${c.payer} ${c.checkNo} ${c.traceNo ?? ''} ${c.patient ?? ''} ${money(c.amount)} ${String(Math.round(c.amount))} ${c.receivedDisplay}`;
}

function bankCell(c: Check) {
  if (c.inBankTone === 'ok') return <span className="dc-badge b-ok">{c.inBankLabel}</span>;
  if (c.inBankTone === 'warn') return <span className="dc-badge b-warn">{c.inBankLabel}</span>;
  return <span style={{ fontSize: 13.5 }}>{c.inBankLabel}</span>;
}

/** One check at its own URL (?check=<id>) — read-only (§3: every row opens the record). */
function CheckDetail({ check, onBack, onOpenMissingHitl }: { check: Check; onBack: () => void; onOpenMissingHitl: () => void }) {
  return (
    <main className="dc-wide" style={{ padding: '24px 28px 40px', display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 760 }}>
      <button type="button" className="dc-back" onClick={onBack}>← All checks</button>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="dc-kind" style={{ alignSelf: 'flex-start' }}>Check</span>
        <h2 className="dc-doctitle">{check.payer} · <span className="num">{money(check.amount)}</span></h2>
        <div className="dc-meta">Received {check.receivedDisplay}</div>
      </section>
      <section className="dc-card dc-card-pad">
        <dl className="dc-kv">
          <dt>Payer</dt><dd>{check.payer}</dd>
          <dt>Check number</dt><dd className="num">{check.checkNo}</dd>
          {check.traceNo ? <><dt>Trace number</dt><dd className="num">{check.traceNo}</dd></> : null}
          <dt>Amount</dt><dd className="num">{money(check.amount)}</dd>
          {check.patient ? <><dt>Patient</dt><dd>{check.patient}</dd></> : null}
          <dt>Posting</dt><dd>{check.postedLabel}</dd>
          <dt>In the bank</dt>
          <dd>
            {check.missingHitl
              ? <button type="button" className="dc-badge b-warn" style={{ border: 0, cursor: 'pointer' }} onClick={onOpenMissingHitl}>{check.inBankLabel}</button>
              : bankCell(check)}
          </dd>
        </dl>
      </section>
      <p className="dc-meta">ELVA matches checks to deposits by amount and date each time you upload a bank statement.</p>
    </main>
  );
}

export function Checks({ rows, missing, hasStatement, onUploadStatement, onOpenMissingHitl }: Props) {
  const [s, patch] = useListUrlState({ q: '', range: DEFAULT_HISTORY_PRESET, page: '1', check: '' });
  const q = s.q;
  const preset = s.range as HistoryDatePreset;
  const page = Math.max(1, Number.parseInt(s.page, 10) || 1);
  const win = rangeForPreset(preset);

  // a selected check opens at its own URL
  const selected = s.check ? (rows.find((c) => c.id === s.check) ?? missing.find((c) => c.id === s.check) ?? null) : null;
  if (selected) {
    return <CheckDetail check={selected} onBack={() => { patch({ check: '' }); }} onOpenMissingHitl={onOpenMissingHitl} />;
  }

  const searched = applySearch(rows, q, checkText);
  const inWindow = searched.filter((c) => inRange(c.receivedIso ?? '', win));
  const sorted = sortBy(inWindow, (c) => c.receivedIso ?? '', -1);
  const pageRows = paginate(sorted, page, PAGE_SIZE);
  const missingTotal = missing.reduce((sum, c) => sum + c.amount, 0);
  const fig = DC_CHECK_FIGURES;

  const openCheck = (id: string) => { patch({ check: id }); };

  return (
    <main className="dc-wide" style={{ padding: '24px 28px 40px', display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1180 }}>
      <section className="dc-figgrid">
        <div className="dc-card dc-fig"><span className="dc-meta">Paper checks received in October</span><span className="big">{fig.received.count} · {money(fig.received.total)}</span><span className="dc-meta">From faxed and scanned EOBs and check photos</span></div>
        <div className="dc-card dc-fig"><span className="dc-meta">In the bank</span><span className="big">{fig.inBank.count} · {money(fig.inBank.total)}</span><span className="dc-meta">Matched to your statement of Oct 1–14</span></div>
        <div className="dc-card dc-fig" style={{ borderColor: 'var(--warn-border)' }}><span className="dc-meta">Not in the bank yet</span><span className="big">{fig.notYet.count} · {money(fig.notYet.total)}</span><span className="dc-meta">All received in the last 2 days</span></div>
      </section>

      <div className="dc-notice" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{hasStatement ? `ELVA matches checks to deposits by amount and date each time you upload a bank statement. Last statement: ${fig.lastStatement}.` : 'No bank statement yet. Upload one so ELVA can match checks to deposits.'}</span>
        <button type="button" className="dc-btn" onClick={onUploadStatement}>Upload a newer statement</button>
      </div>

      {/* Missing — always shown, across every month (§9) */}
      {missing.length > 0 ? (
        <section className="dc-card" aria-labelledby="dc-ol" style={{ overflow: 'hidden', borderColor: 'var(--warn-border)' }}>
          <div style={{ padding: '14px 18px', background: 'var(--wsoft)' }}><h2 id="dc-ol" className="dc-h3" style={{ color: '#5c3c00' }}>Missing checks, across every month: {missing.length} · {money(missingTotal)}</h2></div>
          <div style={{ overflowX: 'auto' }}>
            <table className="dc-tbl"><tbody>
              {missing.map((c) => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => { openCheck(c.id); }}>
                  <td className="num dc-meta" style={{ whiteSpace: 'nowrap' }}>{c.receivedDisplay}</td>
                  <td><strong>{c.payer}</strong><div className="dc-meta num">check {c.checkNo}</div></td>
                  <td className="r num"><strong>{money(c.amount)}</strong></td>
                  <td style={{ fontSize: 13.5 }}>{c.postedLabel}</td>
                  <td><button type="button" className="dc-badge b-warn" style={{ border: 0, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onOpenMissingHitl(); }}>{c.inBankLabel}</button></td>
                </tr>
              ))}
            </tbody></table>
          </div>
          <div className="dc-meta" style={{ padding: '10px 18px 14px', borderTop: '1px solid var(--border)' }}>A check counts as missing when two statements after it was posted don’t show it.</div>
        </section>
      ) : null}

      <section className="dc-card" aria-labelledby="dc-cl" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 8px' }}><h2 id="dc-cl" className="dc-h3" style={{ fontSize: 17 }}>Checks · {windowLabel(preset)}</h2></div>
        <div style={{ padding: '0 14px 12px' }}>
          <FilterBar
            search={q}
            onSearch={(v) => { patch({ q: v, page: '1' }); }}
            searchPlaceholder="Search payer, patient, check number, trace number or amount"
            dateRange={preset}
            onDateRange={(v) => { patch({ range: v, page: '1' }); }}
          />
        </div>
        {pageRows.length === 0 ? (
          <ListEmpty
            query={q.trim() === '' ? undefined : q}
            windowLabel={windowLabel(preset)}
            {...(q.trim() !== '' && preset !== 'all-time'
              ? { onSearchAllTime: () => { patch({ range: 'all-time', page: '1' }); } }
              : {})}
            {...(q.trim() === '' ? { message: `No checks were received in ${windowLabel(preset)}.` } : {})}
          />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="dc-tbl">
                <thead><tr><th scope="col">Received</th><th scope="col">Payer</th><th scope="col" className="r">Amount</th><th scope="col">Posted</th><th scope="col">In the bank</th></tr></thead>
                <tbody>
                  {pageRows.map((c) => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => { openCheck(c.id); }}>
                      <td className="num dc-meta" style={{ whiteSpace: 'nowrap' }}>{c.receivedDisplay}</td>
                      <td><strong>{c.payer}</strong><div className="dc-meta num">check {c.checkNo}</div></td>
                      <td className="r num"><strong>{money(c.amount)}</strong></td>
                      <td style={{ fontSize: 13.5 }}>{c.postedLabel}</td>
                      <td>{hasStatement ? bankCell(c) : <span style={{ fontSize: 13.5 }}>No bank statement yet</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '0 16px 14px' }}>
              <NumberedPager
                page={page}
                pageSize={PAGE_SIZE}
                total={sorted.length}
                onPage={(p) => { patch({ page: String(p) }); }}
              />
            </div>
          </>
        )}
      </section>
    </main>
  );
}
